// 균열 데칼(바닥 투영): 바닥에 눕힌 사각 한 장에 절차 균열 무늬(노이즈 등고선)를 그린다. 균열이 중심에서 바깥으로 자라고, 처음엔 안쪽이 달아올랐다 식으며 흐려진다.
// 바닥이 평면이라 사각 한 장을 바닥 위 3cm에 올려 깊이 검사를 켜고 그린다(벽·기둥 뒤에는 가려진다). 울퉁불퉁한 바닥 투영은 하지 않는다.
// 쓰는 칸: variant 0 = 부채꼴(ahead부터 앞으로 length m, 옆 폭 width m) / 1 = 방사(중심 기준 반경 length m),
//          y = 바닥 높이, life·color(균열 속 빛)·color2·intensity·seed. 드로우콜 1.
import * as THREE from 'three/webgpu';
import { abs, clamp, float, length as vlength, max, mix, smoothstep, step, texture, uv, vec2, vec3 } from 'three/tsl';
import { BatchModule, fxAttributes, type Slot } from './batch';
import { quadLayout } from './layout';
import { clamp01 } from './path';
import { MODULE_CAPS, type ModuleKind } from './types';

/** 바닥 위 높이(m) */
const LIFT = 0.03;

function makeMaterial(noise: THREE.Texture): THREE.MeshBasicNodeMaterial {
  const { ctl, t1, t2 } = fxAttributes();
  const age = ctl.x, env = ctl.y, seed = ctl.z, variant = ctl.w;
  const px = uv().x.mul(2).sub(1);
  const py = uv().y;
  const rad = vec2(px, py.mul(2).sub(1));
  const isRad = step(0.5, variant);
  const dist = mix(py, vlength(rad), isRad);
  const lat = mix(float(1).sub(smoothstep(0.25, 1.0, abs(px))), float(1).sub(smoothstep(0.55, 1.0, vlength(rad))), isRad);
  const p = mix(vec2(px.mul(0.8), py.mul(1.6)), rad.mul(1.1), isRad);
  // 노이즈 등고선 = 균열 선. 두 겹(굵은 선 + 가는 곁가지)
  const warp = texture(noise, p.mul(0.4).add(vec2(3.1, seed))).b;
  const n1 = texture(noise, p.mul(0.9).add(vec2(seed, 0.0))).g;
  const n2 = texture(noise, p.mul(1.9).add(warp.mul(0.35)).add(vec2(0.0, seed.mul(2)))).r;
  const c1 = float(1).sub(smoothstep(0.0, 0.022, abs(n1.sub(0.5))));
  const c2 = float(1).sub(smoothstep(0.0, 0.014, abs(n2.sub(0.5)))).mul(0.8);
  const cracks = max(c1, c2);
  // 자라는 도달 거리
  const grow = clamp(age.mul(6), 0, 1);
  const front = grow.mul(1.15);
  const reach = float(1).sub(smoothstep(front.sub(0.25), front, dist)).mul(float(1).sub(smoothstep(0.8, 1.0, py)));
  const halo = float(1).sub(smoothstep(0.0, 0.9, dist)).mul(0.28);
  const heat = float(1).sub(smoothstep(0.0, 0.55, age));
  const glow = mix(t1.rgb, t2.rgb, 0.3).mul(t1.w);
  const col = mix(vec3(0.07, 0.045, 0.04), glow, cracks.mul(heat));
  const alpha = max(cracks.mul(0.92), halo).mul(reach).mul(lat).mul(env);

  const m = new THREE.MeshBasicNodeMaterial();
  m.transparent = true;
  m.depthWrite = false;
  m.depthTest = true;
  m.side = THREE.DoubleSide;
  m.blending = THREE.NormalBlending;
  m.polygonOffset = true;
  m.polygonOffsetFactor = -2;
  m.polygonOffsetUnits = -2;
  m.forceSinglePass = true;
  m.fog = false;
  m.colorNode = col;
  m.opacityNode = alpha;
  return m;
}

export class CrackModule extends BatchModule {
  readonly kind: ModuleKind = 'crack';

  constructor(noise: THREE.Texture, cap = MODULE_CAPS.crack) {
    super(cap, quadLayout(), makeMaterial(noise), 5, 'fx-crack');
  }

  protected positions(s: Slot, out: Float32Array, o: number): void {
    const sin = Math.sin(s.yaw), cos = Math.cos(s.yaw);
    // 오른쪽 벡터(수평)
    const rx = cos, rz = -sin;
    const L = s.length * s.scale;
    const radial = s.variant >= 0.5;
    // 방사: 중심 기준 [-L, L]² / 부채꼴: ahead부터 앞으로 L, 옆으로 ±폭/2
    const hw = radial ? L : s.width * s.scale * 0.5;
    const near = radial ? -L : 0;
    const bx = s.x + sin * s.ahead, bz = s.z + cos * s.ahead, y = s.y + LIFT;
    // 근-좌, 근-우, 원-좌, 원-우
    out[o] = bx + sin * near - rx * hw; out[o + 1] = y; out[o + 2] = bz + cos * near - rz * hw;
    out[o + 3] = bx + sin * near + rx * hw; out[o + 4] = y; out[o + 5] = bz + cos * near + rz * hw;
    out[o + 6] = bx + sin * L - rx * hw; out[o + 7] = y; out[o + 8] = bz + cos * L - rz * hw;
    out[o + 9] = bx + sin * L + rx * hw; out[o + 10] = y; out[o + 11] = bz + cos * L + rz * hw;
  }

  protected ctl(s: Slot, out: Float32Array): void {
    const age01 = clamp01(s.age / s.life);
    out[0] = age01;
    // 앞 15%는 그대로, 뒤 45%에서 사라진다
    out[1] = Math.min(1, s.age / 0.02) * (1 - clamp01((age01 - 0.55) / 0.45));
    out[2] = s.seed;
    out[3] = s.variant;
  }
}
